import { useTheme } from "@mui/material/styles";
import {
  Avatar,
  Button,
  Collapse,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  SvgIcon,
  Tooltip,
} from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../../locales/tokens";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import ReplyOutlinedIcon from "@mui/icons-material/ReplyOutlined";
import { TextInput } from "../../../../../modules/content/tests/testCreate/ui/components/textInput";
import { Modal } from "../../../../modal/modal";
import { MAX_MESSAGE_LENGTH, useComment } from "../../../presenter/useComment";
import { IComment } from "../../../../../store/comments/repository/props";

export interface CommentCardProps extends IComment {
  onView?: (id: string) => void;
  onReply?: (id: string, msg: string) => void;
  onDelete?: (id: string) => void;
}

export const CommentCard = ({
  id,
  comments,
  author,
  createdAt,
  text,
  isReadByAdmin,
  onView,
  onReply,
  onDelete,
}: CommentCardProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    textareaRef,
    isApproveOpen,
    isReply,
    content,
    handleApprove,
    handleReply,
    handleDelete,
    handleDeleteAnswer,
    onChangeContent,
    handleSubmit,
    handleApproveClose,
    ANIMATION_DURATION,
  } = useComment({ id, onReply, onDelete });
  const isDark = theme.palette.mode === "dark";
  const bgcolor = !isReadByAdmin ? (isDark ? "#3f2e1b" : "#FFF2D9") : undefined;
  const dateColor =
    theme.palette.mode === "dark" ? theme.palette.text.secondary : "#666666";
  const name = `${author.first_name} ${author.last_name} ${author.patronymic}`;

  return (
    <>
      <Stack
        bgcolor={bgcolor}
        p={2}
        borderBottom={`1px solid ${theme.palette.divider}`}
        display="flex"
        gap={2}
        sx={{
          transition: "background-color 0.3s ease",
        }}
      >
        <Stack display="flex" flexDirection="row" gap={2}>
          <Avatar
            sx={{ width: "50px", height: "50px" }}
            alt="User Avatar"
            src={author.photo}
          />
          <Stack fontSize={14} flexGrow={1} spacing={1.5}>
            <Stack
              lineHeight={1}
              display="flex"
              alignItems="center"
              flexDirection="row"
              gap={2}
            >
              <Stack
                display="flex"
                alignItems="center"
                flexDirection="row"
                gap={2}
                flexGrow={1}
              >
                <Stack fontSize={15} fontWeight={600}>
                  {name}
                </Stack>
                <Stack fontSize={13} color={dateColor}>
                  {dayjs(createdAt).format("DD.MM.YYYY HH:mm")}
                </Stack>
              </Stack>
              <Stack display="flex" flexDirection="row" flexWrap="wrap" gap={4}>
                {!isReadByAdmin && (
                  <Button
                    startIcon={<VisibilityOutlinedIcon />}
                    variant="text"
                    style={{
                      textTransform: "none",
                      color: dateColor,
                    }}
                    color="inherit"
                    onClick={() => onView?.(id)}
                  >
                    {t(tokens.comments.viewComment)}
                  </Button>
                )}

                <Stack
                  marginLeft="auto"
                  display="flex"
                  flexDirection="row"
                  gap={2}
                >
                  {!isReply && (
                    <Button
                      startIcon={<ReplyOutlinedIcon />}
                      variant="text"
                      style={{
                        textTransform: "none",
                      }}
                      color="info"
                      onClick={handleReply}
                    >
                      {t(tokens.comments.reply)}
                    </Button>
                  )}
                  <Button
                    startIcon={<DeleteForeverIcon />}
                    variant="text"
                    style={{
                      textTransform: "none",
                    }}
                    color="error"
                    onClick={handleDelete}
                  >
                    {t(tokens.badge.tooltip.delete)}
                  </Button>
                </Stack>
              </Stack>
            </Stack>

            <Stack lineHeight={1.5}>{text}</Stack>
          </Stack>
        </Stack>
        {comments &&
          comments.length > 0 &&
          [...comments]
            .sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            )
            .map(({ id, text, createdAt }) => (
              <Stack
                flexDirection="row"
                gap={2}
                alignItems="flex-start"
                justifyContent="space-between"
                key={createdAt + text}
                pl={1}
                ml={2}
                fontSize={13}
                borderLeft="2px solid"
                borderColor="gray"
              >
                <div style={{ padding: "6px 0" }}>
                  <Stack
                    flexDirection="row"
                    gap={2}
                    fontSize={12}
                    color="gray"
                    mb={1}
                  >
                    {dayjs(createdAt).format("DD.MM.YYYY HH:mm")}
                  </Stack>
                  {text}
                </div>
                <IconButton
                  color="error"
                  size="small"
                  onClick={() => handleDeleteAnswer(id)}
                >
                  <Tooltip title={t(tokens.badge.tooltip.delete)}>
                    <SvgIcon>
                      <DeleteForeverIcon />
                    </SvgIcon>
                  </Tooltip>
                </IconButton>
              </Stack>
            ))}
        <Collapse in={isReply} timeout={ANIMATION_DURATION}>
          <div ref={textareaRef} style={{ paddingTop: 10 }}>
            <TextInput
              isValid
              label={t(tokens.comments.yourComment)}
              value={content}
              onChangeValue={onChangeContent}
              isMultiline={true}
            />
            <Stack marginTop={2} flexDirection="row" gap={2}>
              <Button
                style={{ minWidth: 250 }}
                size="large"
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={content.trim().length === 0}
              >
                {t(tokens.notification.send)}
              </Button>
              <Stack fontSize={12} color="gray" marginLeft="auto">
                {content.length} / {MAX_MESSAGE_LENGTH}
              </Stack>
            </Stack>
          </div>
        </Collapse>
      </Stack>

      <Modal isOpen={isApproveOpen} handleClose={handleApproveClose} size="md">
        <DialogTitle>{t(tokens.comments.deleteApproveTitle)}</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: 15 }}>
            {t(tokens.comments.deleteApproveDescr)}
          </DialogContentText>
          <Stack
            direction="row"
            alignItems="center"
            spacing={2}
            justifyContent="center"
          >
            <Button
              style={{
                flexGrow: 1,
              }}
              color="error"
              size="large"
              variant="contained"
              onClick={handleApprove}
            >
              {t(tokens.badge.tooltip.delete)}
            </Button>
            <Button
              style={{
                flexGrow: 1,
              }}
              color="primary"
              size="large"
              variant="outlined"
              onClick={handleApproveClose}
            >
              {t(tokens.common.cancel)}
            </Button>
          </Stack>
        </DialogContent>
      </Modal>
    </>
  );
};
